<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  parentIcon: {
    type: Array,
    default: () => ['fas', 'gear']
  },
  parentLabel: {
    type: String,
    required: true
  },
  children: {
    type: Array,
    required: true
  },
  isExpanded: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})

// Find the active child based on current route
const activeChild = computed(() => {
  return props.children.find(child =>
    route.path === child.route || route.path.startsWith(child.route)
  )
})

// Determine which icon to display (active child's icon or parent icon)
const displayIcon = computed(() => {
  // Only replace icon in collapsed mode
  if (!props.isExpanded && activeChild.value) {
    return activeChild.value.icon
  }
  return props.parentIcon
})

// Check if any child is active
const hasActiveChild = computed(() => {
  return !!activeChild.value
})

// Check if a specific child route is active
function isChildActive(childRoute) {
  return route.path === childRoute || route.path.startsWith(childRoute)
}

function toggleOpen() {
  if (props.isExpanded) {
    // Expanded mode: toggle inline list
    isOpen.value = !isOpen.value
  } else {
    // Collapsed mode: toggle dropdown
    isOpen.value = !isOpen.value
  }
}

function close() {
  isOpen.value = false
}

// Position the dropdown menu
function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value || props.isExpanded) return

  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = 220
  const viewportHeight = window.innerHeight
  const spacing = 16 // Space between dropdown and sidebar

  // Calculate available space above and below
  const spaceBelow = viewportHeight - rect.bottom
  const spaceAbove = rect.top

  // Position to the RIGHT of the sidebar with spacing
  let left = rect.right + spacing

  // Boundary check
  const viewportWidth = window.innerWidth
  if (left + menuWidth > viewportWidth - 10) {
    left = viewportWidth - menuWidth - 10
  }

  // Determine vertical alignment based on available space
  // Since admin tools are at bottom, prefer aligning bottom of menu with bottom of button
  let top
  if (spaceBelow < 300 && spaceAbove > spaceBelow) {
    // Align bottom of menu with bottom of button
    top = rect.bottom
    menuStyle.value = {
      position: 'fixed',
      bottom: `${viewportHeight - top}px`,
      left: `${left}px`,
      minWidth: `${menuWidth}px`
    }
  } else {
    // Align top of menu with top of button
    top = rect.top
    menuStyle.value = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${menuWidth}px`
    }
  }
}

// Close when clicking outside (only for collapsed dropdown)
function handleClickOutside(event) {
  // Only apply click-outside logic when in collapsed mode with dropdown open
  if (props.isExpanded) return

  if (triggerRef.value && triggerRef.value.contains(event.target)) {
    return
  }
  if (menuRef.value && menuRef.value.contains(event.target)) {
    return
  }
  close()
}

// Close on escape (only for collapsed dropdown)
function handleKeydown(event) {
  if (event.key === 'Escape' && !props.isExpanded && isOpen.value) {
    close()
  }
}

// Update position when opened
watch(isOpen, async (newVal) => {
  if (newVal && !props.isExpanded) {
    await nextTick()
    updateMenuPosition()
  }
})

// Close dropdown when sidebar expands/collapses
watch(() => props.isExpanded, () => {
  close()
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})
</script>

<template>
  <li class="nav-item-wrapper expandable-wrapper">
    <!-- Parent item -->
    <div
      ref="triggerRef"
      class="sidebar-nav"
      :class="{ 'active': hasActiveChild || (isOpen && !isExpanded) }"
      @click="toggleOpen"
    >
      <font-awesome-icon class="icon" :icon="displayIcon" />
      <span v-if="isExpanded" class="nav-text">{{ parentLabel }}</span>
      <font-awesome-icon
        v-if="isExpanded"
        class="chevron"
        :icon="['fas', isOpen ? 'chevron-up' : 'chevron-down']"
      />
    </div>

    <!-- Dropdown menu for collapsed sidebar (teleported) -->
    <Teleport to="body">
      <Transition name="expandable-dropdown">
        <div
          v-if="isOpen && !isExpanded"
          ref="menuRef"
          class="expandable-dropdown-menu"
          :style="menuStyle"
        >
          <!-- Menu items (no header) -->
          <div class="dropdown-items">
            <router-link
              v-for="child in children"
              :key="child.route"
              :to="child.route"
              class="dropdown-item"
              :class="{ 'active': isChildActive(child.route) }"
              @click="close"
            >
              <font-awesome-icon class="item-icon" :icon="child.icon" />
              <span>{{ child.label }}</span>
            </router-link>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Expanded children (inline) -->
    <transition name="expand">
      <ul v-if="isExpanded && isOpen" class="children-list">
        <li v-for="child in children" :key="child.route" class="child-item-wrapper">
          <router-link :to="child.route" class="sidebar-nav child-nav">
            <font-awesome-icon class="icon" :icon="child.icon" />
            <span class="nav-text">{{ child.label }}</span>
          </router-link>
        </li>
      </ul>
    </transition>
  </li>
</template>

<style scoped>
/* Base styles - must replicate Sidebar.vue base styles */

.expandable-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

.sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: #7A8696;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  border-radius: 0.75rem;
  position: relative;
  gap: 0;
  cursor: pointer;
}

.sidebar-nav:hover {
  color: white;
  background-color: #1d2531;
}

/* Parent active state */
.sidebar-nav.active {
  background-color: #1d2531;
  color: white !important;
}

/* Icon base styles */
.sidebar-nav .icon {
  transition: margin-right 0.3s ease;
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Chevron positioning */
.chevron {
  margin-left: auto;
  font-size: 0.85rem;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

/* Text visibility base */
.nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

/* Children list */
.children-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.child-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
  padding-left: 0;
}

/* Child nav items - match exact sidebar styling */
.child-nav {
  padding-left: 2rem !important;
  color: #7A8696;
  text-decoration: none;
}

.child-nav:hover {
  color: white;
  background-color: #1d2531;
}

.child-nav.router-link-active,
.child-nav.router-link-exact-active {
  background-color: #1d2531;
  color: white !important;
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Dropdown menu for collapsed sidebar */
.expandable-dropdown-menu {
  background: #1a1f28;
  border: 1px solid #2a3441;
  border-radius: 0.75rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 9999;
  overflow: hidden;
  min-width: 200px;
}

.dropdown-items {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  color: #E5E7EB;
  border-radius: 0.5rem;
  transition: all 0.15s ease;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.85rem;
}

.dropdown-item:hover {
  background-color: rgba(115, 87, 210, 0.15);
  color: white;
}

.dropdown-item.active {
  background-color: rgba(115, 87, 210, 0.2);
  color: white;
}

.dropdown-item .item-icon {
  width: 1.2rem;
  font-size: 1rem;
  flex-shrink: 0;
  color: rgb(122, 134, 150)
}

/* Dropdown animation */
.expandable-dropdown-enter-active,
.expandable-dropdown-leave-active {
  transition: all 0.2s ease;
}

.expandable-dropdown-enter-from,
.expandable-dropdown-leave-to {
  opacity: 0;
  transform: translateX(8px);
}
</style>

<!-- Unscoped styles for handling collapsed/expanded sidebar states -->
<style>
/* Collapsed sidebar - center icon, hide text */
#sidebar:not(.expanded) .expandable-wrapper .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#sidebar:not(.expanded) .expandable-wrapper .nav-text {
  display: none;
}

/* Expanded sidebar - show text with margin */
#sidebar.expanded .expandable-wrapper .sidebar-nav .icon {
  margin-right: 0.75rem;
}

#sidebar.expanded .expandable-wrapper .nav-text {
  display: inline;
}
</style>
